import './style.scss';

import {
	closestCenter,
	DndContext,
	DragEndEvent,
	DragOverlay,
	DragStartEvent,
	KeyboardSensor,
	PointerSensor,
	UniqueIdentifier,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@wordpress/components';
import { dragHandle } from '@wordpress/icons';
import classNames from 'classnames';
import React, { useState } from 'react';

import type {
	SortableControlProps,
	SortableItem,
	SortableItemWrapperProps,
} from './types';

/**
 * Internal sortable item wrapper component.
 */
const SortableItemWrapper = <T extends SortableItem>({
	item,
	index,
	renderItem,
	allowRemove,
	onRemove,
	onUpdate,
	disabled,
	useDragHandle,
}: SortableItemWrapperProps<T>) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: item.id,
		disabled,
	});

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	// If using drag handle, only attach listeners to the handle
	const dragListeners = useDragHandle ? {} : listeners;
	const handleListeners = useDragHandle ? listeners : {};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={classNames('redfox-thank-you-sortable__item', {
				'redfox-thank-you-sortable__item--dragging': isDragging,
				'redfox-thank-you-sortable__item--disabled': disabled,
			})}
			{...attributes}
			{...dragListeners}
		>
			{useDragHandle && (
				<div
					className="redfox-thank-you-sortable__drag-handle"
					{...handleListeners}
				>
					<Icon icon={dragHandle} size={18} />
				</div>
			)}
			<div className="redfox-thank-you-sortable__item-content">
				{renderItem({
					item,
					index,
					isDragging,
					onRemove: allowRemove ? onRemove : undefined,
					onUpdate,
				})}
			</div>
		</div>
	);
};

/**
 * Sortable control component using @dnd-kit.
 *
 * A reusable drag-and-drop sortable list control that can be used
 * for any list of items that need to be reordered.
 *
 * @see https://dndkit.com
 */
export const SortableControl = <T extends SortableItem>({
	items,
	onChange,
	renderItem,
	className,
	allowRemove = false,
	onRemove,
	onUpdate,
	disabled = false,
	direction = 'vertical',
	useDragHandle = true,
}: SortableControlProps<T>): React.ReactElement => {
	const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id);
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = items.findIndex((item) => item.id === active.id);
			const newIndex = items.findIndex((item) => item.id === over.id);

			onChange(arrayMove(items, oldIndex, newIndex));
		}

		setActiveId(null);
	};

	const handleDragCancel = () => {
		setActiveId(null);
	};

	const activeItem = activeId
		? items.find((item) => item.id === activeId)
		: null;
	const activeIndex = activeId
		? items.findIndex((item) => item.id === activeId)
		: -1;

	const sortingStrategy =
		direction === 'horizontal'
			? horizontalListSortingStrategy
			: verticalListSortingStrategy;

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}
		>
			<SortableContext
				items={items.map((item) => item.id)}
				strategy={sortingStrategy}
			>
				<div
					className={classNames(
						'redfox-thank-you-sortable',
						`redfox-thank-you-sortable--${direction}`,
						className,
						{
							'redfox-thank-you-sortable--disabled': disabled,
						},
					)}
				>
					{items.map((item, index) => (
						<SortableItemWrapper
							key={item.id}
							item={item}
							index={index}
							renderItem={renderItem}
							allowRemove={allowRemove}
							onRemove={onRemove}
							onUpdate={onUpdate}
							disabled={disabled}
							useDragHandle={useDragHandle}
						/>
					))}
				</div>
			</SortableContext>

			<DragOverlay>
				{activeItem && activeIndex >= 0 ? (
					<div className="redfox-thank-you-sortable__item redfox-thank-you-sortable__item--overlay">
						{useDragHandle && (
							<div className="redfox-thank-you-sortable__drag-handle">
								<Icon icon={dragHandle} size={18} />
							</div>
						)}
						<div className="redfox-thank-you-sortable__item-content">
							{renderItem({
								item: activeItem,
								index: activeIndex,
								isDragging: true,
								onRemove: allowRemove ? onRemove : undefined,
								onUpdate,
							})}
						</div>
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	);
};

